/**
 * service-dossiers-en.authored-e.ts — Wave 166
 *
 * Final batch of hand-authored English case dossiers, completing the 25-case
 * English mirror set. No machine translation, no AI credits at build or run
 * time.
 *
 * Rules enforced here:
 *  - no pricing anywhere (meta or body)
 *  - no Review / AggregateRating / ClaimReview data
 *  - no guarantees; every duration is a working-day range with a caveat
 *  - Apostille wording stays conditional: the Convention enters into force for
 *    Thailand on 28 February 2027; until then the consular legalisation chain
 *    applies.
 */
import type { ServiceDossier } from "./service-dossiers";

const MFA_CITE = {
  label: "Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)",
  url: "https://consular.mfa.go.th/",
};
const DOPA_CITE = {
  label: "Department of Provincial Administration (DOPA)",
  url: "https://www.dopa.go.th/",
};
const COJ_CITE = { label: "Courts of Justice (Thailand)", url: "https://www.coj.go.th/" };
const IMMI_CITE = { label: "Immigration Bureau (Thailand)", url: "https://www.immigration.go.th/" };
const HCCH_CITE = { label: "HCCH — Hague Conference on Private International Law", url: "https://www.hcch.net/" };

const CASES_LINK: [string, string] = ["All case studies", "/en/case-studies"];

export const EN_AUTHORED_DOSSIERS_E: ServiceDossier[] = [
  {
    slug: "birth-registration-child-born-abroad",
    cluster: "mfa",
    title: "Registering the birth of a Thai child born abroad",
    metaTitle: "Case study: birth registration for a child born abroad",
    metaDescription:
      "How a child born outside Thailand is registered as Thai: the foreign birth certificate chain, translation, consular certification, house registration and passport application.",
    h1: "Case study: registering a child born abroad and adding them to the Thai house registration",
    answer:
      "A child born abroad to a Thai parent is registered through the Thai embassy or consulate in that country, which issues a Thai birth registration record. The foreign birth certificate must first be certified in the country of birth, then translated into Thai and certified by the Department of Consular Affairs before the child can be added to a house registration and issued a Thai passport and ID number.",
    audience:
      "Thai nationals whose child was born overseas, bi-national families returning to Thailand, and parents preparing school enrolment or inheritance matters in Thailand.",
    facts: [
      { label: "First filing point", value: "Thai embassy or consulate in the country of birth" },
      { label: "Translation", value: "Foreign birth certificate into Thai, 1–3 working days" },
      { label: "Consular certification", value: "Usually 2–5 working days depending on filing channel" },
      { label: "House registration", value: "District office (amphoe) in Thailand" },
    ],
    documents: [
      "Original foreign birth certificate, certified by the issuing country's competent authority",
      "Certified Thai translation of the birth certificate",
      "Thai parent's ID card and house registration",
      "Both parents' passports",
      "Marriage certificate, or a paternity acknowledgement record where the parents are unmarried",
      "Child's foreign passport, where one has already been issued",
    ],
    steps: [
      {
        title: "Certify the birth certificate in the country of birth",
        detail:
          "The certificate is authenticated by that country's competent authority. Where the country issues Apostilles, that is the layer used before the Thai mission; otherwise the consular chain applies.",
        who: "Issuing-country authority",
        eta: "Varies by country",
      },
      {
        title: "Translate into Thai and certify",
        detail:
          "The translation preserves names, dates and place of birth exactly as printed, with a translator's note where the foreign name order differs from Thai practice.",
        who: "Translation team / Department of Consular Affairs",
        eta: "3–8 working days",
      },
      {
        title: "File the Thai birth registration",
        detail:
          "The Thai embassy or consulate records the birth and issues the Thai birth registration document used for every later step.",
        who: "Thai embassy or consulate",
        eta: "Depends on the mission's appointment schedule",
      },
      {
        title: "Add the child to the house registration and apply for the passport",
        detail:
          "At the district office the child is entered on a house registration and given a 13-digit ID number; the Thai passport application follows.",
        who: "District office / Department of Consular Affairs",
        eta: "1–5 working days once the file is complete",
      },
    ],
    pitfalls: [
      "Filing a hospital-issued birth notification instead of the civil birth certificate.",
      "A name transliterated differently on the passport and the translation, with no translator's note reconciling the two.",
      "Skipping the certification layer in the country of birth, which the Thai mission then refuses.",
      "Unmarried parents filing without a paternity acknowledgement, which blocks the father's details being recorded.",
      "Leaving registration until the child needs a Thai passport urgently, when the chain still takes weeks.",
    ],
    compare: [
      {
        option: "Register at the Thai mission abroad",
        useWhen: "The family is still in the country of birth",
        time: "Depends on appointment availability",
        note: "The most direct route; the mission issues the Thai birth record",
      },
      {
        option: "Prepare documents abroad, file in Thailand",
        useWhen: "The family has already returned to Thailand",
        time: "3–10 working days once documents arrive",
        note: "Requires the foreign certification layer to be complete before departure",
      },
      {
        option: "Full document handling by our team",
        useWhen: "The parents cannot attend agencies during working hours",
        time: "Runs in parallel with the family's schedule",
        note: "Powers of attorney are prepared for the steps that allow representation",
      },
    ],
    faqs: [
      {
        q: "Does a child born abroad automatically hold Thai nationality?",
        a: "A child with at least one Thai parent is generally entitled to Thai nationality, but the entitlement only becomes usable once the birth is registered with a Thai mission and the child is entered on a house registration with a 13-digit ID number. Until then the child has no Thai documents to travel or enrol with, so registration should not be postponed.",
      },
      {
        q: "Can the birth be registered years after it happened?",
        a: "Yes. Late registration is possible, but district offices ask for more supporting evidence — school records, medical records and the parents' documents from the period concerned. The chain of certification for the foreign birth certificate is the same; only the evidentiary burden increases with time.",
      },
      {
        q: "Will the Apostille change this process?",
        a: "The Apostille Convention enters into force for Thailand on 28 February 2027. From that date, a single Apostille from a member country replaces the embassy layer of the chain. Until then the consular legalisation chain applies, so files prepared now should follow the existing route.",
      },
      {
        q: "What if the parents are not married?",
        a: "The mother's details are recorded from the birth certificate. Recording the father requires a legal acknowledgement of paternity, made at a district office or through the courts. Preparing that record before the registration filing avoids a second visit and keeps the house registration entry consistent.",
      },
      {
        q: "Can you handle the whole chain for us?",
        a: "Yes. We map the certification route for the specific country of birth, produce and certify the Thai translation, prepare the mission and district-office filings, and coordinate the passport application. With more than 15 years of case work, the advisory step at the start is what prevents documents being rejected at the mission counter.",
      },
    ],
    related: [
      ["MFA legalisation", "/en/services/mfa-legalization"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [MFA_CITE, DOPA_CITE, HCCH_CITE],
  },
  {
    slug: "sworn-translation-europe-submission",
    cluster: "translate",
    title: "Sworn translation for submission to European authorities",
    metaTitle: "Case study: sworn translation for European authorities",
    metaDescription:
      "How Thai documents are prepared for European authorities that require a sworn or court-appointed translator: the order of certification, common refusals and how to check the receiving rule first.",
    h1: "Case study: preparing Thai documents for a European sworn-translation requirement",
    answer:
      "Several European countries accept only translations produced by a sworn or court-appointed translator registered in that country. The workable route is to certify the Thai original first, then have the sworn translator in the destination country translate the certified set. Reversing that order is the single most common reason a European authority refuses the file.",
    audience:
      "People filing with civil registries, courts, universities or immigration authorities in Germany, France, Spain, Italy, the Netherlands and similar jurisdictions.",
    facts: [
      { label: "Key rule", value: "Certify the Thai original before the sworn translation" },
      { label: "Thai-side preparation", value: "Usually 3–10 working days depending on the chain" },
      { label: "Sworn translation", value: "Handled by a translator registered in the destination country" },
      { label: "Document age", value: "Many European registries accept originals issued within 3–6 months" },
    ],
    documents: [
      "Original Thai civil document issued by the district office, not a photocopy",
      "Certified Thai-to-English translation where the destination accepts an intermediate language",
      "Department of Consular Affairs certification of the Thai document set",
      "Embassy or consulate legalisation where that country still requires it",
      "Passport copies of everyone named in the documents",
    ],
    steps: [
      {
        title: "Confirm the receiving authority's exact wording",
        detail:
          "European authorities differ: some name a sworn translator, some accept a translation certified by their embassy, some accept only their own registry's list. We obtain the written requirement before producing anything.",
        who: "Advisory team",
        eta: "1–3 working days",
      },
      {
        title: "Obtain fresh originals",
        detail:
          "Where the registry limits document age, a newly issued copy is requested from the district office so the file does not expire mid-process.",
        who: "District office",
        eta: "Same day to 3 working days",
      },
      {
        title: "Certify the Thai set",
        detail:
          "Translation into the accepted intermediate language, then certification by the Department of Consular Affairs, and an embassy layer where required.",
        who: "Translation team / Department of Consular Affairs / embassy",
        eta: "3–10 working days",
      },
      {
        title: "Hand over to the sworn translator abroad",
        detail:
          "The fully certified set is sent to the sworn translator in the destination country, who translates the certified document including the certification stamps themselves.",
        who: "Sworn translator in destination country",
        eta: "Varies by country and translator",
      },
    ],
    pitfalls: [
      "Translating first and certifying afterwards, which leaves the certification attached to the wrong document.",
      "A sworn translator translating only the text and omitting the seals and certification notes, which most registries require.",
      "Using an original older than the registry's accepted window.",
      "Assuming one country's rule applies EU-wide; each member state sets its own translator requirement.",
      "Sending scans where the registry requires the physical certified original.",
    ],
    compare: [
      {
        option: "Certified Thai translation only",
        useWhen: "The destination accepts a Thai-issued certified translation",
        time: "1–3 working days",
        note: "The shortest route, but only where the authority allows it",
      },
      {
        option: "Thai certification + sworn translation abroad",
        useWhen: "The registry names a sworn or court-appointed translator",
        time: "Thai side 3–10 working days, plus the translator's own time",
        note: "The order of the two layers is what decides acceptance",
      },
      {
        option: "Embassy-certified translation",
        useWhen: "That country's embassy in Bangkok certifies translations directly",
        time: "Depends entirely on the embassy's schedule",
        note: "Availability differs sharply between missions",
      },
    ],
    faqs: [
      {
        q: "What exactly is a sworn translator?",
        a: "A sworn or court-appointed translator is registered with a court or ministry in their own country and is authorised to certify translations under their personal registration number. Thailand does not operate an identical register, which is why some European authorities insist the translation be produced in their own jurisdiction rather than in Thailand.",
      },
      {
        q: "Why must the Thai document be certified before translation?",
        a: "The sworn translator abroad translates the document as presented, including its certification stamps. If the certification is added afterwards, the sworn translation refers to an uncertified document and the registry treats the file as incomplete. Getting the order right is usually the difference between one filing and three.",
      },
      {
        q: "Does the Apostille remove the need for a sworn translation?",
        a: "No. From 28 February 2027, when the Apostille Convention enters into force for Thailand, the Apostille replaces the embassy legalisation layer for the document's authenticity. It says nothing about the language, so a country that requires a sworn translation will still require one.",
      },
      {
        q: "How long is a certified set valid?",
        a: "Certification itself has no fixed expiry, but the receiving registry usually limits how old the underlying civil document may be, commonly three to six months. The practical rule is to time the chain so the filing happens well within that window rather than starting from the oldest copy on hand.",
      },
      {
        q: "Can your team coordinate the translator abroad as well?",
        a: "Yes. We handle the Thai side end to end, brief the sworn translator on which pages and stamps must be included, and track the file until the registry accepts it. Over more than 15 years we have built the country-by-country notes that stop the most common European refusals before they happen.",
      },
    ],
    related: [
      ["Certified translation", "/en/services/translation"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [MFA_CITE, HCCH_CITE],
  },
  {
    slug: "divorce-decree-recognition-abroad",
    cluster: "notary",
    title: "Recognition of a Thai divorce abroad, and a foreign divorce in Thailand",
    metaTitle: "Case study: recognising a divorce across two countries",
    metaDescription:
      "How a Thai divorce is recognised overseas and how a foreign divorce decree is recorded in Thailand: administrative versus court divorce, the document chain and the registry pitfalls.",
    h1: "Case study: making a divorce effective in both countries",
    answer:
      "A divorce recorded in one country is not automatically effective in the other. A Thai administrative divorce is evidenced by the divorce registration and certificate from the district office; a foreign decree normally requires certification in the issuing country, a certified Thai translation and consular certification before a Thai registry will record the change of status.",
    audience:
      "Bi-national couples, Thai nationals who divorced abroad, and anyone whose remarriage, visa or property transaction depends on a clean marital-status record.",
    facts: [
      { label: "Thai routes", value: "Administrative divorce at the district office, or divorce by court judgment" },
      { label: "Core evidence", value: "Divorce registration and divorce certificate" },
      { label: "Translation", value: "1–3 working days per document set" },
      { label: "Consular certification", value: "Usually 2–5 working days depending on channel" },
    ],
    documents: [
      "Divorce certificate and divorce registration, or the certified court judgment",
      "Certified translation into the destination language, or into Thai for filing in Thailand",
      "Marriage certificate for the marriage being dissolved",
      "ID card and house registration of the Thai party",
      "Passports of both parties",
      "Where a court judgment applies, a certificate that the judgment is final",
    ],
    steps: [
      {
        title: "Identify which route produced the divorce",
        detail:
          "An administrative divorce is evidenced by district-office records; a contested divorce is evidenced by the judgment plus a finality certificate. The two follow different certification paths.",
        who: "Advisory team",
        eta: "Same day",
      },
      {
        title: "Obtain fresh certified copies",
        detail:
          "Registries usually want a recently issued certified copy rather than the original handed over years earlier.",
        who: "District office / court",
        eta: "1–5 working days",
      },
      {
        title: "Translate and certify",
        detail:
          "Translation with names matching the passport exactly, then certification by the Department of Consular Affairs, with an embassy layer where the destination still requires it.",
        who: "Translation team / Department of Consular Affairs",
        eta: "3–10 working days",
      },
      {
        title: "File with the receiving registry",
        detail:
          "Abroad, the civil registry records the status change; in Thailand, the district office annotates the marital-status record so later filings show the correct status.",
        who: "Destination registry / district office",
        eta: "Varies by registry",
      },
    ],
    pitfalls: [
      "Filing a judgment without the certificate confirming it is final, which registries reject outright.",
      "A foreign decree never recorded in Thailand, so Thai records still show the person as married.",
      "Name discrepancies where one party reverted to a maiden name without the supporting name-change record.",
      "Assuming an administrative divorce is enough where the destination requires a court decision.",
      "Starting a remarriage filing before the previous status has been corrected in both countries.",
    ],
    compare: [
      {
        option: "Administrative divorce (district office)",
        useWhen: "Both parties agree and both can attend",
        time: "Often the same day",
        note: "Some foreign registries examine it closely because no court was involved",
      },
      {
        option: "Court divorce",
        useWhen: "The parties disagree, or the destination requires a judicial decision",
        time: "Months, depending on the court's schedule",
        note: "The judgment plus finality certificate is the document that travels",
      },
      {
        option: "Recording a foreign decree in Thailand",
        useWhen: "The divorce already happened abroad",
        time: "3–10 working days once documents are certified",
        note: "Needed before remarriage or property transactions in Thailand",
      },
    ],
    faqs: [
      {
        q: "Is a Thai administrative divorce accepted abroad?",
        a: "Often, but not universally. Because no court examines an administrative divorce, some registries scrutinise it and a few require a court decision or a supplementary declaration. Checking the destination registry's written rule before certifying documents avoids producing a set that country will not accept.",
      },
      {
        q: "Must a foreign divorce be recorded in Thailand?",
        a: "For practical purposes, yes. Until the change is recorded, Thai records still show the earlier marriage, which blocks remarriage, complicates property transactions and can affect inheritance. The recording is done at a district office with a certified and translated copy of the foreign decree.",
      },
      {
        q: "What is a finality certificate and why does it matter?",
        a: "It is the court's confirmation that no appeal remains open. Registries treat a judgment without it as provisional, because a decision under appeal can still be reversed. It is issued by the court that gave the judgment and must travel through the same certification chain as the judgment itself.",
      },
      {
        q: "How does this affect a single-status certificate?",
        a: "A single-status certificate is issued from the marital-status record held in Thailand. If a foreign divorce has not been recorded there, the record still shows a subsisting marriage and no single-status certificate can be issued, which in turn blocks a marriage registration abroad.",
      },
      {
        q: "Can your team run both sides?",
        a: "Yes. We identify which evidence each registry needs, obtain fresh certified copies, produce the certified translations, run the consular chain and prepare the filing in both countries. With more than 15 years handling cross-border family records, we sequence the two sides so neither registry is waiting on the other.",
      },
    ],
    related: [
      ["Notarial services", "/en/services/notary-public"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [MFA_CITE, DOPA_CITE, COJ_CITE],
  },
  {
    slug: "student-visa-document-set",
    cluster: "visa",
    title: "Student visa document set for study in Thailand and abroad",
    metaTitle: "Case study: preparing a student visa document set",
    metaDescription:
      "The document set behind a student visa: acceptance letter, academic records, financial evidence and the certification chain, plus the errors that cause a refusal.",
    h1: "Case study: assembling a student visa file that survives first review",
    answer:
      "A student visa file rests on three pillars: proof of admission from the institution, verified academic history, and financial evidence covering tuition and living costs. Documents crossing a border normally need a certified translation and, for government filings, certification by the Department of Consular Affairs. Financial evidence is judged on how long funds have been held, not only on the balance shown.",
    audience:
      "Students applying to universities or language schools abroad, foreign students entering Thailand on an ED visa, and parents acting as financial sponsors.",
    facts: [
      { label: "Admission evidence", value: "Acceptance letter or enrolment confirmation from the institution" },
      { label: "Academic records", value: "Transcript and graduation certificate, translated and certified" },
      { label: "Financial evidence", value: "Bank statements, commonly covering the last 3–6 months" },
      { label: "Certification chain", value: "Translation 1–3 working days, consular step usually 2–5 working days" },
    ],
    documents: [
      "Acceptance letter or enrolment confirmation from the institution",
      "Academic transcript and graduation certificate, with certified translations",
      "Passport valid well beyond the intended course period",
      "Bank statements and a bank certification letter for the sponsor's account",
      "Sponsor letter with proof of the relationship, where a parent funds the study",
      "Language test result where the institution or embassy requires one",
      "Medical certificate and police clearance certificate where the destination requires them",
    ],
    steps: [
      {
        title: "Read the embassy checklist for the exact study level",
        detail:
          "Requirements differ between language courses, undergraduate and postgraduate study, and between missions of the same country. The checklist defines everything that follows.",
        who: "Advisory team",
        eta: "1–2 working days",
      },
      {
        title: "Prepare academic records",
        detail:
          "Transcripts are requested from the institution, translated with grading scales explained, then certified where the destination requires an official chain.",
        who: "Institution / translation team",
        eta: "3–10 working days",
      },
      {
        title: "Build the financial file",
        detail:
          "Statements are arranged to show a stable holding period rather than a single deposit, with a bank certification letter and a sponsor letter where funds are not the student's own.",
        who: "Bank / advisory team",
        eta: "2–7 working days",
      },
      {
        title: "Book the appointment and file",
        detail:
          "The complete set is filed in the order the checklist lists it, so the reviewing officer can follow it without searching.",
        who: "Embassy or consulate / Immigration Bureau",
        eta: "Depends on the mission's calendar",
      },
    ],
    pitfalls: [
      "A large deposit made days before filing, which reads as borrowed funds rather than available funds.",
      "Transcripts without a translated grading scale, leaving the reviewer unable to interpret the results.",
      "A passport expiring during the course, which caps or refuses the visa period.",
      "A sponsor letter with no documentary proof of the family relationship.",
      "Filing a course that does not match the visa category, for example a short course under a long-stay study category.",
    ],
    compare: [
      {
        option: "Certified translation only",
        useWhen: "The institution accepts translations directly",
        time: "1–3 working days",
        note: "Typical at the admission stage",
      },
      {
        option: "Translation + consular certification",
        useWhen: "The embassy or an official body reviews the documents",
        time: "3–8 working days",
        note: "The standard route for visa filings",
      },
      {
        option: "Full chain with embassy layer",
        useWhen: "The destination still requires double certification",
        time: "5–15 working days",
        note: "Timing depends on the specific mission",
      },
    ],
    faqs: [
      {
        q: "How much financial evidence is enough?",
        a: "Each embassy publishes its own threshold, usually tuition plus living costs for the first year. Reviewers weigh how long the funds have been held as much as the balance, so statements covering three to six months of stable holding are far stronger than a single recent transfer of the same amount.",
      },
      {
        q: "Do transcripts always need consular certification?",
        a: "Not for admission — institutions generally accept a certified translation sent directly. Certification becomes necessary when a government body reviews the records, for example at the visa stage or when a professional registry later assesses the qualification.",
      },
      {
        q: "Can a parent sponsor the study?",
        a: "Yes, and it is common. The file then needs the sponsor's bank evidence, a signed sponsorship letter and documentary proof of the relationship such as a birth certificate or house registration, each translated where the receiving authority does not read Thai.",
      },
      {
        q: "What happens if the course is deferred?",
        a: "A deferral usually requires a new acceptance letter with the revised dates, and financial evidence refreshed to the new filing date. Documents already certified stay valid for authenticity purposes, but statements and letters tied to a date normally have to be reissued.",
      },
      {
        q: "Can your team prepare the whole file?",
        a: "Yes. We read the specific mission's checklist, obtain and translate the academic records, structure the financial evidence so the holding period is visible, run the certification chain and hand over a filing-ready set. More than 15 years of visa case work goes into spotting the weak point before the reviewer does.",
      },
    ],
    related: [
      ["Visa services", "/en/services/visa"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [MFA_CITE, IMMI_CITE],
  },
  {
    slug: "adoption-documents-cross-border",
    cluster: "notary",
    title: "Cross-border adoption documents between Thailand and abroad",
    metaTitle: "Case study: cross-border adoption document chain",
    metaDescription:
      "The document chain behind an inter-country adoption involving Thailand: home study, consents, court and registration records, translation and consular certification.",
    h1: "Case study: preparing the document chain for an inter-country adoption",
    answer:
      "Inter-country adoption runs through the central authorities of both countries under the 1993 Hague Adoption Convention, and the documents are examined more strictly than in any other family matter. The chain covers the home study, consents, medical and background records, and the resulting Thai adoption registration, each translated and certified before the receiving country will record the child's new status.",
    audience:
      "Families adopting across borders, Thai relatives involved in a family adoption, and anyone recording a completed adoption in a second country.",
    facts: [
      { label: "Framework", value: "1993 Hague Adoption Convention, applied through both central authorities" },
      { label: "Thai authority", value: "Department of Children and Youth, Ministry of Social Development and Human Security" },
      { label: "Translation", value: "1–3 working days per document set" },
      { label: "Overall timeline", value: "Months, set by the two central authorities rather than by document work" },
    ],
    documents: [
      "Home study report prepared by an accredited agency in the receiving country",
      "Written consents from those whose consent the law requires",
      "Adoptive parents' marriage certificate, income evidence and police clearance certificates",
      "Medical certificates for the adoptive parents and the child",
      "Child's birth certificate and house registration",
      "Thai adoption registration and any related court order",
      "Certified translations of every document that crosses a border",
    ],
    steps: [
      {
        title: "Open the file with both central authorities",
        detail:
          "The receiving country's authority and the Thai authority coordinate the matching and approval; documents are prepared to their joint requirements, not to a general checklist.",
        who: "Central authorities of both countries",
        eta: "Set by the authorities",
      },
      {
        title: "Prepare and certify the receiving-country documents",
        detail:
          "The home study, background checks and financial evidence are certified in the receiving country, then translated into Thai for filing.",
        who: "Accredited agency / receiving-country authority",
        eta: "Weeks to months",
      },
      {
        title: "Complete the Thai side",
        detail:
          "Consents, registration and, where required, the court step are completed and recorded, producing the Thai adoption records.",
        who: "Department of Children and Youth / district office / court",
        eta: "Set by the authorities",
      },
      {
        title: "Certify the Thai records for use abroad",
        detail:
          "The adoption registration and supporting records are translated and certified by the Department of Consular Affairs so the receiving country can record the child's status and issue travel documents.",
        who: "Translation team / Department of Consular Affairs / embassy",
        eta: "3–10 working days",
      },
    ],
    pitfalls: [
      "Attempting a private arrangement outside the central authorities, which the receiving country will not recognise.",
      "A home study that has expired by the time the Thai side is ready, requiring the whole report to be redone.",
      "Consents obtained without the formalities the law requires, which invalidates them at the filing stage.",
      "Name and date discrepancies between the birth certificate, house registration and adoption records.",
      "Assuming the Thai adoption record alone lets the child travel; the receiving country's recording and travel document are separate steps.",
    ],
    compare: [
      {
        option: "Convention inter-country adoption",
        useWhen: "The adoptive parents live in a Convention country",
        time: "Months, driven by the two authorities",
        note: "The only route that produces status recognised on both sides",
      },
      {
        option: "Family adoption involving relatives",
        useWhen: "The child is adopted within the extended family",
        time: "Still routed through the central authorities",
        note: "Relationship evidence is examined in detail",
      },
      {
        option: "Recording a completed foreign adoption in Thailand",
        useWhen: "The adoption was finalised abroad",
        time: "3–10 working days once documents are certified",
        note: "Keeps Thai civil records consistent for later matters",
      },
    ],
    faqs: [
      {
        q: "Can an adoption be arranged privately between the families?",
        a: "For an inter-country case, no. The 1993 Hague Adoption Convention requires the central authorities of both countries to handle matching and approval, and a private arrangement will not be recognised by the receiving country — leaving the child without lawful status there even if Thai paperwork exists.",
      },
      {
        q: "How long does the whole process take?",
        a: "Months, and sometimes longer. The timeline is set by the two central authorities, not by document preparation. What document work can control is that nothing expires and nothing is returned for correction, which is where most avoidable delay comes from.",
      },
      {
        q: "Which documents must be translated?",
        a: "Every document crossing a border in either direction: the home study, consents, medical and police records into Thai, and the Thai birth, registration and adoption records into the receiving country's language. Names must match the passports exactly, with a translator's note wherever transliteration differs.",
      },
      {
        q: "Does the child's Thai record change after adoption?",
        a: "Yes. The adoption is recorded and the child's civil records are updated accordingly. Because later matters — passports, schooling, inheritance — read from those records, they should be checked for consistency immediately after registration rather than years later.",
      },
      {
        q: "How can your team help?",
        a: "We work alongside the accredited agency handling the case: mapping which documents each authority needs, producing certified translations in both directions, running the consular chain and keeping every document inside its validity window. With more than 15 years on cross-border family files, our role is to keep the paperwork from becoming the reason a case stalls.",
      },
    ],
    related: [
      ["Notarial services", "/en/services/notary-public"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [HCCH_CITE, MFA_CITE, DOPA_CITE],
  },
];
